import { Injectable } from '@angular/core';
import { find } from 'lodash-es';
import {
  Extension,
  mapReservationCodeToName,
  MiddlewareBaseWithExtensionResponse,
  ReservationLookupResult,
  ReservationPerson,
  ReservationPersonName,
  ReservationSearch,
} from '@ireckonu/shared-ui';

import { HARDCODED_LANGUAGES } from '@shared/app-config/consts';

import {
  LoyaltyLevelInterface,
  MembershipInterface,
  ReservationsSearchQueryApiInterface,
  RFM_LOYALTY_LEVEL,
  RfmTierInterface,
  RoomReservationApiInterface,
} from '../models';

interface ExtendedReservationLookupResult extends ReservationLookupResult {
  NumberOfInfants?: number | null;
}

type ReservationLoyaltyLevel = Omit<LoyaltyLevelInterface, 'TypeCode'> & {
  Type?: string;
};

type PersonWithMemberships = ReservationPerson & {
  Memberships?: MembershipInterface[];
  LoyaltyLevels?: ReservationLoyaltyLevel[];
};

@Injectable({ providedIn: 'root' })
export class ReservationSearchMappingService {
  mapSearchQueryToRequest(
    query: ReservationsSearchQueryApiInterface,
    hotelCode?: string,
  ): ReservationSearch {
    const request: ReservationSearch = {
      HotelCodes: this.buildHotelCodes(query, hotelCode),
      Limit: 50,
      Skip: 0,
      LookupParameters: this.getDefaultLookupParameters(),
      ReservationStatus: [],
    };

    if (query.firstName || query.lastName || query.email || query.guestId) {
      request.Person = {};
      if (query.firstName) {
        request.Person.FirstName = query.firstName;
      }
      if (query.lastName) {
        request.Person.LastName = query.lastName;
      }
      if (query.email) {
        request.Person.EmailAddress = query.email;
      }
      if (query.guestId) {
        request.Person.PersonId = query.guestId;
      }
    }

    // TODO: phone and membershipId don't have direct fields in ReservationSearch

    if (query.reservationId) {
      const reservationIdNum = parseInt(query.reservationId, 10);
      if (!isNaN(reservationIdNum)) {
        request.ReservationIds = [reservationIdNum];
      }
    }

    if (query.reservationNumber) {
      request.ExternalReferences = [{ Code: query.reservationNumber }];
    }

    if (query.reservationIds?.length) {
      request.ReservationIds = query.reservationIds;
    }

    if (query.reservationStatus && query.reservationStatus.length > 0) {
      request.ReservationStatus = query.reservationStatus;
    }

    return request;
  }

  private buildHotelCodes(
    query: ReservationsSearchQueryApiInterface,
    hotelCode?: string,
  ): NonNullable<ReservationSearch['HotelCodes']> {
    if (!hotelCode) {
      return [];
    }

    const unitCodes = query.roomNumber?.trim()
      ? [query.roomNumber.trim()]
      : undefined;

    return [
      { HotelCode: hotelCode, ...(unitCodes ? { UnitCodes: unitCodes } : {}) },
    ];
  }

  private mapSearchResultToReservation(
    result: ExtendedReservationLookupResult,
    extension?: Extension,
  ): RoomReservationApiInterface {
    const subReservation = result.SubReservations?.[0];
    const effectiveResult = this.hasReservationDetails(result)
      ? result
      : (subReservation ?? result);

    const guest = (effectiveResult.PrimaryGuest ??
      effectiveResult.Booker ??
      result.Booker) as PersonWithMemberships | undefined;
    const numberOfAdults = effectiveResult.NumberOfPersons ?? 0;
    const numberOfChildren = effectiveResult.NumberOfChildren ?? 0;
    const numberOfInfants = effectiveResult.NumberOfInfants ?? 0;

    const ratePlan =
      find(extension?.Rateplans, {
        Code: effectiveResult.RatePlan?.Code,
      }) ??
      effectiveResult.RatePlan ??
      result.RatePlan;
    const unitType = find(extension?.UnitTypes, {
      Code: effectiveResult.UnitTypeCode,
    });

    const kanjiName = this.findNameByType(guest?.Names, 'kanji');
    const katakanaName = this.findNameByType(guest?.Names, 'katakana');

    return {
      id: String(result.Id ?? ''),
      reservationNumber:
        effectiveResult.ExternalReferences?.[0]?.Code ??
        result.ExternalReferences?.[0]?.Code,
      hotelName: effectiveResult.HotelCode ?? result.HotelCode,
      status: mapReservationCodeToName(
        effectiveResult.Status ?? result.Status ?? '',
      ),
      checkIn: effectiveResult.ArrivalDate ?? result.ArrivalDate,
      checkOut: effectiveResult.DepartureDate ?? result.DepartureDate,
      nights: effectiveResult.NumberOfNights ?? result.NumberOfNights,
      source: effectiveResult.Source ?? result.Source,
      type: effectiveResult.UnitTypeCode
        ? {
            code: effectiveResult.UnitTypeCode,
            label: unitType ? unitType.Name : effectiveResult.UnitTypeCode,
          }
        : undefined,
      rate: ratePlan
        ? {
            code: ratePlan.Code ?? '',
            label: ratePlan.Name ?? ratePlan.Description ?? '',
          }
        : undefined,
      unitCode: effectiveResult.UnitCode
        ? Number.parseInt(effectiveResult.UnitCode, 10)
        : undefined,
      totalGroup: numberOfAdults + numberOfChildren + numberOfInfants,
      numberOfAdults,
      numberOfChildren,
      numberOfInfants,
      guest: guest
        ? {
            id: String(guest.Id ?? ''),
            name: this.buildGuestNameFromPerson(guest),
            avatar: this.getAvatarFromAttributes(guest.Attributes),
            kanjiName: this.formatJapaneseName(kanjiName),
            katakanaName: this.formatJapaneseName(katakanaName),
            email: this.getPrimaryEmail(guest.EmailAddresses),
            phone: this.getPrimaryPhone(guest.PhoneNumbers),
            nationality: guest.CountryCode,
            language:
              HARDCODED_LANGUAGES.find(
                (lang) => lang.Value === guest.LanguageCode,
              )?.Name ?? guest.LanguageCode,
            dateOfBirth: guest.DateOfBirth,
            membershipId: this.getPrimaryMembershipCode(guest.Memberships),
            membershipTier: this.getPrimaryMembershipLevel(guest.Memberships),
            rfmTier: this.getRfmTier(guest.LoyaltyLevels),
          }
        : undefined,
    };
  }

  private findNameByType(
    names: ReservationPersonName[] | undefined,
    typeCode: string,
  ): ReservationPersonName | undefined {
    if (!names?.length) {
      return undefined;
    }

    const typeCodeLower = typeCode.toLowerCase();

    if (typeCodeLower === 'default') {
      return names.find(
        (n) => !n.Type || n.Type.toLowerCase() === 'default' || n.Type === '',
      );
    }
    return names.find((n) => n.Type?.toLowerCase() === typeCodeLower);
  }

  private formatJapaneseName(
    name: ReservationPersonName | undefined,
  ): string | undefined {
    if (!name) {
      return undefined;
    }

    const parts: string[] = [];
    if (name.LastName) {
      parts.push(name.LastName);
    }
    if (name.FirstName) {
      parts.push(name.FirstName);
    }

    return parts.length > 0 ? parts.join(' ') : undefined;
  }

  private hasReservationDetails(result: ReservationLookupResult): boolean {
    return !!(result.UnitTypeCode || result.RatePlan || result.UnitCode);
  }

  private getAvatarFromAttributes(
    attributes?: Array<{ Code?: string; Value?: string }> | null,
  ): string | undefined {
    if (!attributes || attributes.length === 0) {
      return undefined;
    }

    const avatarAttr = attributes.find((attr) => attr.Code === 'Url');

    return avatarAttr?.Value;
  }

  private buildGuestNameFromPerson(
    person: ReservationLookupResult['PrimaryGuest'],
  ): string | undefined {
    if (!person) {
      return undefined;
    }

    const names = person.Names;
    if (names && names.length > 0) {
      const primaryName = names[0];
      const parts = [
        primaryName.Title,
        primaryName.FirstName,
        primaryName.LastName,
      ].filter(Boolean);
      if (parts.length > 0) {
        return parts.join(' ');
      }
    }

    return undefined;
  }

  private getPrimaryEmail(
    emailAddresses?: Array<{ Value?: string; IsPrimary?: boolean }>,
  ): string | undefined {
    if (!emailAddresses || emailAddresses.length === 0) {
      return undefined;
    }

    const primary = emailAddresses.find((e) => e.IsPrimary);
    return primary?.Value ?? emailAddresses[0]?.Value;
  }

  private getPrimaryPhone(
    phoneNumbers?: Array<{
      Number?: string;
      CountryAccessCode?: string;
      IsPrimary?: boolean;
    }>,
  ): string | undefined {
    if (!phoneNumbers || phoneNumbers.length === 0) {
      return undefined;
    }

    const primary = phoneNumbers.find((p) => p.IsPrimary);
    const phone = primary ?? phoneNumbers[0];
    if (!phone?.Number) {
      return undefined;
    }

    return phone.CountryAccessCode
      ? `${phone.CountryAccessCode}${phone.Number}`
      : phone.Number;
  }

  private getPrimaryMembershipCode(
    memberships?: MembershipInterface[],
  ): string | undefined {
    if (!memberships || memberships.length === 0) {
      return undefined;
    }

    const primary = memberships.find((m) => m.IsPrimary);
    return primary?.Code ?? memberships[0]?.Code;
  }

  private getPrimaryMembershipLevel(
    memberships?: MembershipInterface[],
  ): string | undefined {
    if (!memberships || memberships.length === 0) {
      return undefined;
    }

    const primary = memberships.find((m) => m.IsPrimary);
    return primary?.Level ?? memberships[0]?.Level;
  }

  private getRfmTier(
    loyaltyLevels?: ReservationLoyaltyLevel[],
  ): RfmTierInterface | undefined {
    const rfmLevel = loyaltyLevels?.find(
      (level) =>
        level.Source === RFM_LOYALTY_LEVEL.source &&
        level.Type === RFM_LOYALTY_LEVEL.typeCode,
    );
    if (!rfmLevel?.LevelCode) {
      return undefined;
    }

    return {
      code: rfmLevel.LevelCode,
      label: rfmLevel.LevelCode,
      score: rfmLevel.Score,
    };
  }

  mapSearchResultsToReservations(
    results: MiddlewareBaseWithExtensionResponse<ReservationLookupResult[]>,
  ): RoomReservationApiInterface[] {
    return (results.Data ?? []).map((result) =>
      this.mapSearchResultToReservation(result, results.Extension),
    );
  }

  private getDefaultLookupParameters(): ReservationSearch['LookupParameters'] {
    return {
      IncludeBooker: true,
      Booker: {
        IncludeEmailAddresses: true,
        IncludeAddresses: true,
        IncludePhoneNumbers: true,
        IncludeAttributes: true,
        IncludeDataConsents: false,
        IncludePaymentMethods: false,
        IncludeStaySummaries: false,
        IncludeExternalReferences: false,
        IncludeLoyaltyLevels: true,
        IncludePreferences: false,
        IncludeMemberships: true,
      },
      IncludePrimaryGuest: true,
      PrimaryGuest: {
        IncludeEmailAddresses: true,
        IncludeAddresses: true,
        IncludePhoneNumbers: true,
        IncludeAttributes: true,
        IncludeDataConsents: false,
        IncludePaymentMethods: false,
        IncludeStaySummaries: false,
        IncludeExternalReferences: false,
        IncludeLoyaltyLevels: true,
        IncludePreferences: false,
        IncludeMemberships: true,
      },
      IncludeOtherLinkedPersons: false,
      OtherLinkedPersons: {
        IncludeEmailAddresses: false,
        IncludeAddresses: false,
        IncludePhoneNumbers: false,
        IncludeAttributes: false,
        IncludeDataConsents: false,
        IncludePaymentMethods: false,
        IncludeStaySummaries: false,
        IncludeExternalReferences: false,
        IncludeLoyaltyLevels: false,
        IncludePreferences: false,
        IncludeMemberships: true,
      },
      IncludeMemberships: true,
      IncludeContacts: false,
      IncludeRatePlan: true,
      IncludeExtraServices: false,
      IncludeExternalReferences: true,
      IncludePaymentInformations: false,
      IncludePaymentMethods: false,
      IncludeCompany: false,
      IncludeAttributes: false,
      Company: {
        IncludeExternalReferences: false,
        IncludeAddresses: false,
        IncludePhoneNumbers: false,
        IncludeEmailAddresses: false,
      },
      IncludeAgency: false,
      Agency: {
        IncludeExternalReferences: false,
        IncludeAddresses: false,
        IncludePhoneNumbers: false,
        IncludeEmailAddresses: false,
      },
      IncludeSourceCompany: false,
      SourceCompany: {
        IncludeExternalReferences: false,
        IncludeAddresses: false,
        IncludePhoneNumbers: false,
        IncludeEmailAddresses: false,
      },
      Extension: {
        IncludeUnitTypes: true,
        IncludeProperties: true,
        IncludeRateplans: true,
      },
    };
  }
}
